import {
  signIn as amplifySignIn,
  signInWithRedirect,
  signOut as amplifySignOut,
  fetchAuthSession,
  getCurrentUser as amplifyGetCurrentUser,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';
import type { IAuthGateway, User, LoginResult, SignUpParams, SignUpResult, SessionValidationResult } from '@domain/ports/IAuthGateway';
import {
  UserAlreadyExistsError,
  WeakPasswordError,
  VerificationCodeError,
  SignupError,
} from '@domain/errors/SignupErrors';

/**
 * AWS Cognito Authentication Gateway (Amplify JS)
 *
 * Production implementation of IAuthGateway using AWS Amplify v6.
 * Supports:
 * - Email/password authentication
 * - OAuth providers (Google, Apple)
 * - Remember me functionality
 * - Custom userId extraction from JWT claims
 */
export class CognitoAuthGateway implements IAuthGateway {
  // Note: Remember me is handled by localStorage in rememberUser()
  // This field is kept for internal tracking but not used in current implementation
  // @ts-expect-error - Reserved for future remember-me implementation
  private _rememberMeEnabled: boolean = false;

  /**
   * Extract user from Amplify session and ID token claims
   * Includes custom:userId from JWT claims (Story 1.3 requirement)
   *
   * Uses ID token instead of fetchUserAttributes() to avoid "Access Token does not have required scopes"
   * error with OAuth providers. Works for both native auth (email/password) and OAuth (Google/Apple).
   */
  private async extractUserFromSession(): Promise<User> {
    try {
      const [currentUser, session] = await Promise.all([
        amplifyGetCurrentUser(),
        fetchAuthSession(),
      ]);

      const idToken = session.tokens?.idToken;
      if (!idToken) {
        throw new Error('auth.errors.unknownError');
      }

      // Extract all attributes from ID token (works for both native auth and OAuth)
      const customUserId = idToken.payload['custom:userId'] as string | undefined;
      const email = idToken.payload.email as string | undefined;
      const name = idToken.payload.name as string | undefined;

      return {
        id: currentUser.userId,
        email: email || '',
        name: name || email || currentUser.username,
        customUserId,
      };
    } catch (error) {
      throw new Error('auth.errors.unknownError');
    }
  }

  /**
   * Authenticate user with email and password
   * Converts Amplify Auth errors to domain error codes
   */
  async login(email: string, password: string): Promise<LoginResult> {
    try {
      const signInResult = await amplifySignIn({
        username: email,
        password,
        options: {
          authFlowType: 'USER_SRP_AUTH',
        },
      });

      // Check if sign-in is complete
      if (!signInResult.isSignedIn) {
        throw new Error('auth.errors.unknownError');
      }

      // Extract user and tokens
      const user = await this.extractUserFromSession();
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString() || '';
      const refreshToken = session.tokens?.accessToken?.toString();

      return {
        user,
        token,
        refreshToken,
      };
    } catch (error: unknown) {
      // Convert Amplify errors to domain errors
      if (error instanceof Error) {
        const errorName = error.name;
        const errorMessage = error.message.toLowerCase();

        if (errorName === 'UserNotFoundException' || errorMessage.includes('user does not exist')) {
          throw new Error('auth.errors.userNotFound');
        }
        if (errorName === 'NotAuthorizedException' || errorMessage.includes('incorrect username or password')) {
          throw new Error('auth.errors.invalidCredentials');
        }
        if (errorName === 'UserNotConfirmedException' || errorMessage.includes('not confirmed')) {
          throw new Error('auth.errors.userNotConfirmed');
        }
        if (errorName === 'TooManyRequestsException' || errorMessage.includes('attempt limit exceeded')) {
          throw new Error('auth.errors.tooManyAttempts');
        }
        if (errorMessage.includes('network') || errorMessage.includes('connection')) {
          throw new Error('auth.errors.networkError');
        }
      }
      throw new Error('auth.errors.unknownError');
    }
  }

  /**
   * Sign in with OAuth provider (Google or Apple)
   * Initiates OAuth flow with redirect
   *
   * Note: Caller should ensure any existing session is signed out before calling this method
   */
  async signInWithProvider(provider: 'google' | 'apple'): Promise<void> {
    try {
      // Map provider names to Amplify provider format
      const providerMap: Record<string, 'Google' | 'Apple'> = {
        google: 'Google',
        apple: 'Apple',
      };

      console.log('[CognitoAuthGateway] Initiating OAuth sign-in', { provider: providerMap[provider] });

      await signInWithRedirect({
        provider: providerMap[provider],
      });

      // Note: This function triggers a redirect, so execution doesn't continue
      // The OAuth callback will handle the rest of the flow
    } catch (error: unknown) {
      console.error('[CognitoAuthGateway] OAuth sign-in error:', error);

      if (error instanceof Error) {
        console.error('[CognitoAuthGateway] Error message:', error.message);
        console.error('[CognitoAuthGateway] Error name:', error.name);

        // UserAlreadyAuthenticatedException - should be handled by caller
        if (error.name === 'UserAlreadyAuthenticatedException') {
          throw new Error('auth.errors.alreadyAuthenticated');
        }

        const errorMessage = error.message.toLowerCase();

        if (errorMessage.includes('cancelled') || errorMessage.includes('cancel')) {
          throw new Error('auth.errors.oauthCancelled');
        }
      }
      throw new Error('auth.errors.oauthError');
    }
  }

  /**
   * Sign out current user
   * Clears all auth tokens and session data
   * For OAuth sessions, this triggers federated logout (redirects to provider)
   */
  async logout(): Promise<void> {
    try {
      await amplifySignOut();
    } catch (error) {
      // Still throw error but log it for debugging
      console.error('Logout error:', error);
      throw new Error('auth.errors.unknownError');
    }
  }

  /**
   * Sign out locally only (no federated logout)
   * Clears Amplify credentials from storage without triggering OAuth provider logout
   * Use this when switching from OAuth to email authentication
   */
  async signOutLocally(): Promise<void> {
    try {
      // Clear Amplify credentials from localStorage/sessionStorage manually
      // This avoids the OAuth federated logout redirect
      const keysToRemove: string[] = [];

      // Collect all Amplify-related keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('CognitoIdentityServiceProvider') || key.startsWith('amplify'))) {
          keysToRemove.push(key);
        }
      }

      // Remove all collected keys
      keysToRemove.forEach(key => localStorage.removeItem(key));

      console.log('[CognitoAuthGateway] Local credentials cleared (no federated logout)');
    } catch (error) {
      console.error('[CognitoAuthGateway] Local sign-out error:', error);
      // Don't throw - continue with login attempt
    }
  }

  /**
   * Get currently authenticated user
   * Returns null if not authenticated
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const user = await this.extractUserFromSession();
      return user;
    } catch {
      // User not authenticated
      return null;
    }
  }

  /**
   * Refresh authentication token
   * Uses Amplify's automatic token refresh
   */
  async refreshToken(): Promise<string> {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const newToken = session.tokens?.idToken?.toString();

      if (!newToken) {
        throw new Error('auth.errors.unknownError');
      }

      return newToken;
    } catch (error) {
      throw new Error('auth.errors.unknownError');
    }
  }

  /**
   * Set remember me preference
   * Note: Amplify handles token persistence automatically
   * This method stores the preference for future use
   */
  async rememberUser(remember: boolean): Promise<void> {
    this._rememberMeEnabled = remember;
    // Store preference in localStorage for persistence
    try {
      localStorage.setItem('anyw3ar_remember_me', remember.toString());
    } catch (error) {
      // Ignore localStorage errors (e.g., in private mode)
      console.warn('Failed to save remember me preference:', error);
    }
  }

  /**
   * Sign up a new user with email, password, and username
   * Story 3.2.b: Unified GDPR consent passed via clientMetadata
   *
   * Single consent checkbox controls all three clientMetadata values:
   * - termsAccepted
   * - privacyAccepted
   * - marketingAccepted
   */
  async signUp(params: SignUpParams): Promise<SignUpResult> {
    const { email, password, username, gdprConsent } = params;

    try {
      // Single consent boolean maps to all three clientMetadata values
      const allConsentsAccepted = gdprConsent.toString();

      const result = await amplifySignUp({
        username: email, // Email as username
        password,
        options: {
          userAttributes: {
            email,
            // Story 3.2.b: Use preferred_username attribute (persisted through email verification)
            // This fixes issue where clientMetadata is not available in PostConfirmation trigger
            preferred_username: username,
          },
          clientMetadata: {
            // GDPR consent passed to Post-Confirmation Lambda
            // Single checkbox acceptance means all three are true
            termsAccepted: allConsentsAccepted,
            privacyAccepted: allConsentsAccepted,
            marketingAccepted: allConsentsAccepted,
            // Display name fallback (kept for backwards compatibility)
            displayName: username,
          },
        },
      });

      return {
        userSub: result.userId || '',
        // @ts-expect-error - AWS Amplify v6 type mismatch, needs investigation
        codeDeliveryDetails: result.nextStep.codeDeliveryDetails
          ? {
              // @ts-expect-error - AWS Amplify v6 type mismatch, needs investigation
              destination: result.nextStep.codeDeliveryDetails.destination || '',
              // @ts-expect-error - AWS Amplify v6 type mismatch, needs investigation
              deliveryMedium: result.nextStep.codeDeliveryDetails.deliveryMedium || '',
              // @ts-expect-error - AWS Amplify v6 type mismatch, needs investigation
              attributeName: result.nextStep.codeDeliveryDetails.attributeName || '',
            }
          : undefined,
      };
    } catch (error: unknown) {
      // Convert Amplify errors to domain errors
      if (error instanceof Error) {
        const errorName = error.name;
        const errorMessage = error.message.toLowerCase();

        // User already exists
        if (errorName === 'UsernameExistsException' || errorMessage.includes('user already exists')) {
          throw new UserAlreadyExistsError(email);
        }

        // Weak password
        if (
          errorName === 'InvalidPasswordException' ||
          errorMessage.includes('password') ||
          errorMessage.includes('invalid password')
        ) {
          throw new WeakPasswordError([
            'At least 8 characters',
            'At least one uppercase letter',
            'At least one lowercase letter',
            'At least one number',
          ]);
        }

        // Invalid parameter (e.g., invalid email format)
        if (errorName === 'InvalidParameterException') {
          throw new SignupError('Invalid signup parameters', error);
        }

        // Network error
        if (errorMessage.includes('network') || errorMessage.includes('connection')) {
          throw new SignupError('Network error during signup', error);
        }
      }

      // Generic signup error
      throw new SignupError('Unknown signup error', error instanceof Error ? error : undefined);
    }
  }

  /**
   * Confirm user's email with verification code
   */
  async confirmSignUp(email: string, code: string): Promise<void> {
    try {
      await amplifyConfirmSignUp({
        username: email,
        confirmationCode: code,
      });
    } catch (error: unknown) {
      if (error instanceof Error) {
        const errorName = error.name;
        const errorMessage = error.message.toLowerCase();

        // Invalid code
        if (errorName === 'CodeMismatchException' || errorMessage.includes('invalid code')) {
          throw new VerificationCodeError('Invalid verification code', 'invalid');
        }

        // Expired code
        if (errorName === 'ExpiredCodeException' || errorMessage.includes('expired')) {
          throw new VerificationCodeError('Verification code has expired', 'expired');
        }

        // User not found
        if (errorName === 'UserNotFoundException') {
          throw new SignupError('User not found', error);
        }

        // User already confirmed
        if (errorName === 'NotAuthorizedException' || errorMessage.includes('already confirmed')) {
          // Not an error - user is already verified
          return;
        }
      }

      throw new SignupError('Email verification failed', error instanceof Error ? error : undefined);
    }
  }

  /**
   * Resend verification code to user's email
   */
  async resendSignUp(email: string): Promise<void> {
    try {
      await resendSignUpCode({
        username: email,
      });
    } catch (error: unknown) {
      if (error instanceof Error) {
        const errorName = error.name;
        const errorMessage = error.message.toLowerCase();

        // User not found
        if (errorName === 'UserNotFoundException') {
          throw new SignupError('User not found', error);
        }

        // User already confirmed
        if (errorName === 'InvalidParameterException' || errorMessage.includes('already confirmed')) {
          // Not an error - user is already verified
          return;
        }

        // Too many attempts
        if (errorName === 'LimitExceededException' || errorMessage.includes('attempt limit')) {
          throw new SignupError('Too many resend attempts. Please try again later.', error);
        }
      }

      throw new SignupError('Failed to resend verification code', error instanceof Error ? error : undefined);
    }
  }

  /**
   * Initiate password reset process
   * Story 3.3.b: Password reset frontend UI
   *
   * Security: Does not reveal if user exists to prevent email enumeration
   */
  async forgotPassword(email: string): Promise<void> {
    try {
      await resetPassword({
        username: email,
      });
      // Success - code sent to email (if user exists)
    } catch (error: unknown) {
      if (error instanceof Error) {
        const errorName = error.name;
        const errorMessage = error.message.toLowerCase();

        // User not found - don't reveal this to prevent email enumeration
        if (errorName === 'UserNotFoundException') {
          // Return success anyway for security
          return;
        }

        // Rate limiting
        if (errorName === 'LimitExceededException' || errorMessage.includes('attempt limit')) {
          throw new Error('passwordReset.errors.tooManyRequests');
        }

        // Network error
        if (errorMessage.includes('network') || errorMessage.includes('connection')) {
          throw new Error('passwordReset.errors.networkError');
        }
      }

      // Generic error
      throw new Error('passwordReset.errors.unknownError');
    }
  }

  /**
   * Complete password reset with verification code
   * Story 3.3.b: Password reset frontend UI
   */
  async forgotPasswordSubmit(email: string, code: string, newPassword: string): Promise<void> {
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
      // Success - password has been reset
    } catch (error: unknown) {
      if (error instanceof Error) {
        const errorName = error.name;
        const errorMessage = error.message.toLowerCase();

        // Invalid verification code
        if (errorName === 'CodeMismatchException' || errorMessage.includes('invalid code')) {
          throw new Error('passwordReset.errors.invalidCode');
        }

        // Expired verification code
        if (errorName === 'ExpiredCodeException' || errorMessage.includes('expired')) {
          throw new Error('passwordReset.errors.expiredCode');
        }

        // Weak password
        if (
          errorName === 'InvalidPasswordException' ||
          errorMessage.includes('password') ||
          errorMessage.includes('invalid password')
        ) {
          throw new Error('passwordReset.errors.passwordWeak');
        }

        // User not found
        if (errorName === 'UserNotFoundException') {
          throw new Error('passwordReset.errors.userNotFound');
        }

        // Too many attempts (rate limiting)
        if (errorName === 'LimitExceededException' || errorMessage.includes('attempt limit')) {
          throw new Error('passwordReset.errors.tooManyRequests');
        }

        // Network error
        if (errorMessage.includes('network') || errorMessage.includes('connection')) {
          throw new Error('passwordReset.errors.networkError');
        }
      }

      // Generic error
      throw new Error('passwordReset.errors.unknownError');
    }
  }

  /**
   * Resend password reset code
   * Story 3.3.b: Password reset frontend UI
   *
   * Security: Same as forgotPassword - does not reveal user existence
   */
  async resendPasswordResetCode(email: string): Promise<void> {
    // AWS Cognito uses the same method for initial and resend
    return this.forgotPassword(email);
  }

  /**
   * Get the refresh threshold in milliseconds
   * Configurable via VITE_SESSION_REFRESH_THRESHOLD_MINUTES env variable
   * Default: 300 minutes (5 hours)
   */
  private getRefreshThresholdMs(): number {
    const envMinutes = import.meta.env.VITE_SESSION_REFRESH_THRESHOLD_MINUTES;
    const minutes = envMinutes ? parseInt(envMinutes, 10) : 300; // Default 5 hours
    return minutes * 60 * 1000;
  }

  /**
   * Validate current session and optionally refresh tokens
   * Story 4.9: Session Expiration Handling
   *
   * Handles Amplify v6 bug where tokens may be undefined even with valid refresh token.
   * Strategy:
   * 1. Try normal session fetch
   * 2. If tokens undefined, attempt forceRefresh
   * 3. If still undefined, session is truly expired
   */
  async validateSession(): Promise<SessionValidationResult> {
    const thresholdMs = this.getRefreshThresholdMs();
    const thresholdMinutes = thresholdMs / 60000;
    console.log(`[Session] validateSession called (threshold: ${thresholdMinutes}min)`);

    try {
      // Try normal session fetch
      let session = await fetchAuthSession();

      // Handle Amplify bug: tokens undefined even with valid refreshToken
      if (!session.tokens?.idToken) {
        console.log('[Session] No token found, attempting force refresh...');
        // Attempt force refresh
        session = await fetchAuthSession({ forceRefresh: true });
      }

      if (!session.tokens?.idToken) {
        // Both attempts failed - session truly expired
        console.log('[Session] No valid session found');
        return { isValid: false, error: 'Session expired' };
      }

      // Check if proactive refresh is needed
      const expiresAt = await this.getTokenExpiresAt();

      if (expiresAt) {
        const timeUntilExpiryMs = expiresAt - Date.now();
        const timeUntilExpiryMinutes = Math.round(timeUntilExpiryMs / 60000);
        console.log(`[Session] Token expires in ${timeUntilExpiryMinutes}min`);

        if (timeUntilExpiryMs < thresholdMs) {
          console.log(`[Session] Within threshold (${thresholdMinutes}min) - attempting proactive refresh...`);

          try {
            session = await fetchAuthSession({ forceRefresh: true });

            if (!session.tokens?.idToken) {
              console.log('[Session] Proactive refresh failed - no token returned');
              return { isValid: false, error: 'Token refresh failed' };
            }

            console.log('[Session] Proactive refresh successful');
          } catch (refreshError) {
            console.log('[Session] Proactive refresh failed:', refreshError instanceof Error ? refreshError.message : 'Unknown error');
            return { isValid: false, error: 'Token refresh failed' };
          }
        }
      }

      // Extract user from token
      const user = await this.extractUserFromSession();
      const token = session.tokens.idToken.toString();

      return { isValid: true, user, token };
    } catch (error) {
      return {
        isValid: false,
        error: error instanceof Error ? error.message : 'Session validation failed',
      };
    }
  }

  /**
   * Get the expiration timestamp of the current token
   * Story 4.9: Proactive token refresh on mount
   *
   * Parses the JWT to extract the exp claim.
   */
  async getTokenExpiresAt(): Promise<number | null> {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        return null;
      }

      // Parse JWT payload (second segment)
      const payload = JSON.parse(atob(idToken.split('.')[1]));

      // exp is in seconds, convert to milliseconds
      return payload.exp ? payload.exp * 1000 : null;
    } catch {
      return null;
    }
  }
}
